import { useEffect } from "react";
import { useFormikContext } from "formik";
import { useDebounce } from "./useDebounce";
import { useCheckFieldExists } from "./useCheckFieldExists";

interface UseAsyncFieldValidationOptions {
  fieldName: string;
  endpoint: string;
  paramName: string;
  errorMessage: string;
  debounceMs?: number;
  isValueCheckable?: (value: string) => boolean;
  /** Transform the raw field value before sending (e.g. lowercase email) */
  normalize?: (value: string) => string;
}

export function useAsyncFieldValidation({
  fieldName,
  endpoint,
  paramName,
  errorMessage,
  debounceMs = 500,
  isValueCheckable,
  normalize = (v) => v.trim(),
}: UseAsyncFieldValidationOptions) {
  const { values, setFieldError, errors } = useFormikContext<Record<string, string>>();
  const rawValue = values[fieldName] ?? "";
  const normalizedValue = normalize(rawValue);
  const debouncedValue = useDebounce(normalizedValue, debounceMs);

  const { data: exists, isFetching: isChecking } = useCheckFieldExists({
    endpoint,
    paramName,
    value: debouncedValue,
    isValueCheckable,
  });

  useEffect(() => {
    if (isChecking) return;
    if (debouncedValue !== normalizedValue) return; 
    if (exists === true) {
      setFieldError(fieldName, errorMessage);
    } else if (errors[fieldName] === errorMessage) {
      setFieldError(fieldName, undefined);
    }
  }, [exists, isChecking, debouncedValue, normalizedValue, fieldName, errorMessage, setFieldError, errors]);

  return { isChecking };
}